import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, setAccessToken } from './api';
import { resetMediaProgress } from './offline/persist';
import { clearOfflineData, readOfflineUser, saveOfflineUser } from './offline/store';
import type { AuthSessionResponse, User } from './types';

interface AuthContextValue {
  user: User | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  isAdmin: boolean;
  login: (handle: string, password: string) => Promise<void>;
  register: (input: {
    handle: string;
    password: string;
    inviteCode: string;
    displayName?: string;
    homeCountry?: string;
  }) => Promise<{ recoveryCode?: string; inviteCodes?: string[] }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  patchUser: (patch: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Session state.
 *
 * On mount we attempt a silent refresh: if the httpOnly cookie is still valid
 * the user lands signed in without a flash of the logged-out UI, and if it is
 * not we fall through to `anonymous` in one round-trip.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');

  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const restored = await api.restoreSession();
      if (cancelled) return;
      if (restored === 'unreachable') {
        // No network. The device's copy of the user lets the owner's own links
        // open what is kept offline; the next request with a network refreshes
        // the session for real (a 401 refreshes and replays).
        const kept = await readOfflineUser();
        if (cancelled) return;
        setUser(kept);
        setStatus(kept ? 'authenticated' : 'anonymous');
        return;
      }
      if (restored === 'denied') {
        // The session ended elsewhere (signed out on another device, or
        // expired). What it kept on this device goes with it.
        if (await readOfflineUser()) void clearOfflineData();
        if (!cancelled) setStatus('anonymous');
        return;
      }
      try {
        const me = await api.get<User>('/auth/me');
        if (cancelled) return;
        setUser(me);
        setStatus('authenticated');
        void saveOfflineUser(me);
      } catch {
        if (!cancelled) setStatus('anonymous');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (handle: string, password: string) => {
    const session = await api.post<AuthSessionResponse>('/auth/login', { handle, password });
    setAccessToken(session.tokens.accessToken);
    setUser(session.user);
    setStatus('authenticated');
    void saveOfflineUser(session.user);
  }, []);

  const register = useCallback(
    async (input: { handle: string; password: string; inviteCode: string; displayName?: string }) => {
      const session = await api.post<AuthSessionResponse>('/auth/register', input);
      setAccessToken(session.tokens.accessToken);
      setUser(session.user);
      setStatus('authenticated');
      void saveOfflineUser(session.user);
      return { recoveryCode: session.recoveryCode, inviteCodes: session.inviteCodes };
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setAccessToken(null);
      setUser(null);
      setStatus('anonymous');
      // Anything kept for offline reading includes documents and booking
      // details. It must not survive the session that asked for it — on the
      // device or in this tab's memory.
      void clearOfflineData();
      queryClient.clear();
      resetMediaProgress();
    }
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    try {
      const me = await api.get<User>('/auth/me');
      setUser(me);
      void saveOfflineUser(me);
    } catch {
      /* stale session; the next request will trigger a refresh or a redirect */
    }
  }, []);

  const patchUser = useCallback((patch: Partial<User>) => {
    setUser((current) => (current ? { ...current, ...patch } : current));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      isAdmin: user?.role === 'admin',
      login,
      register,
      logout,
      refreshUser,
      patchUser,
    }),
    [user, status, login, register, logout, refreshUser, patchUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
